import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertCircle, BookOpen, Star, Lightbulb, Users, Sparkles, MessageCircle, Heart, Printer, Copy, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { DevotionalData } from '../lib/devotionalTemplate';
import { parseISO } from 'date-fns';

interface SharedDevotionalRecord {
  share_id: string;
  sermon_title: string;
  scripture: string | null;
  date: string | null;
  devotional_data: DevotionalData;
  view_count: number;
  created_at: string;
}

export function SharedDevotionalPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<SharedDevotionalRecord | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!shareId) { setError('Invalid link'); setLoading(false); return; }
    const load = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('shared_devotional_pages')
          .select('*')
          .eq('share_id', shareId)
          .single();
        if (fetchError) throw fetchError;
        if (!data) throw new Error('Not found');
        setPage(data);
        await supabase
          .from('shared_devotional_pages')
          .update({ view_count: (data.view_count || 0) + 1 })
          .eq('share_id', shareId);
      } catch {
        setError('This devotional page could not be found or has expired.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFAF3]">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A2F]" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFAF3]">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="h-12 w-12 text-[#7A2C2C] mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[#1A1A1A] mb-2">Page Not Found</h1>
          <p className="text-[#666]">{error || 'This devotional page could not be found.'}</p>
        </div>
      </div>
    );
  }

  const d = page.devotional_data;
  const displayDate = page.date
    ? parseISO(page.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div style={{ fontFamily: "'Crimson Text', Georgia, serif", background: '#FDFAF3', minHeight: '100vh', color: '#1A1A1A' }}>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Crimson+Text:ital,wght@0,400;0,600;1,400;1,600&family=Montserrat:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Action bar */}
      <div style={{ background: '#1E3A2F', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
        <button
          onClick={() => window.print()}
          style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#A07820', color: 'white', border: 'none', borderRadius: '8px', padding: '0.5rem 1.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <Printer style={{ width: 14, height: 14 }} />
          Print / Save PDF
        </button>
        <button
          onClick={copyLink}
          style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '0.5rem 1.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        >
          {linkCopied ? <><Check style={{ width: 14, height: 14 }} />Copied!</> : <><Copy style={{ width: 14, height: 14 }} />Copy Link</>}
        </button>
        <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>
          Enable "Background graphics" when printing
        </span>
      </div>

      {/* Cover */}
      <div style={{ background: '#1E3A2F', color: 'white', textAlign: 'center', padding: '3.5rem 2rem 3rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#C49A30', marginBottom: '1.4rem' }}>✦ &nbsp; SermonBuild &nbsp; ✦</div>
        <div style={{ fontSize: '1.4rem', color: '#C49A30', opacity: 0.7, marginBottom: '0.8rem' }}>Family Devotional Guide</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 5vw, 3rem)', fontWeight: 700, lineHeight: 1.2, color: 'white', marginBottom: '1rem', position: 'relative', zIndex: 1 }}>
          {d.devotionalTitle}
        </h1>
        <p style={{ fontFamily: "'Crimson Text', serif", fontStyle: 'italic', fontSize: '1.15rem', color: 'rgba(255,255,255,0.75)', marginBottom: '1.6rem' }}>{d.theme}</p>
        <p style={{ fontFamily: "'Crimson Text', serif", fontSize: '1.05rem', color: '#C49A30', fontStyle: 'italic', maxWidth: 520, margin: '0 auto 1.8rem', borderTop: '1px solid rgba(255,255,255,0.15)', borderBottom: '1px solid rgba(255,255,255,0.15)', padding: '0.8rem 1rem', position: 'relative', zIndex: 1 }}>
          {d.keyVerse}
        </p>
        <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)' }}>
          {page.sermon_title}
          {displayDate && <><span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 0.5rem' }}>·</span>{displayDate}</>}
          {page.scripture && <><span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 0.5rem' }}>·</span>{page.scripture}</>}
        </p>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '3rem 2rem 4rem' }}>

        {/* Main Devotion */}
        <Section icon={<BookOpen />} label="This Week's Devotion" title={d.devotionalTitle}>
          <div style={{ fontSize: '1.05rem', lineHeight: 1.75, color: '#3A3A3A' }}>
            {d.mainDevotion.split(/\n\n+/).filter(Boolean).map((p, i) => (
              <p key={i} style={{ marginBottom: '1.1rem', textAlign: 'justify' }}>{p}</p>
            ))}
          </div>
        </Section>

        <Divider />

        {/* Memory Verse */}
        <div style={{ background: '#1E3A2F', color: 'white', borderRadius: 12, padding: '2rem 2.2rem', margin: '2rem 0' }}>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#C49A30', marginBottom: '0.9rem' }}>
            <Star style={{ display: 'inline', width: 12, height: 12, marginRight: 6 }} />Memory Verse
          </div>
          <blockquote style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.25rem', fontStyle: 'italic', lineHeight: 1.5, color: 'white', marginBottom: '0.5rem' }}>
            "{d.memoryVerse.text}"
          </blockquote>
          <cite style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C49A30', fontStyle: 'normal', display: 'block', marginBottom: '1.4rem' }}>
            {d.memoryVerse.reference}
          </cite>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
            {[{ label: '🤲 Hand Motions', text: d.memoryVerse.motionTip }, { label: '🎵 Melody / Rhythm', text: d.memoryVerse.melodyHint }].map(({ label, text }) => (
              <div key={label} style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.78rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.45 }}>
                <strong style={{ display: 'block', color: '#C49A30', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.25rem' }}>{label}</strong>
                {text}
              </div>
            ))}
          </div>
        </div>

        {/* Object Lesson */}
        <div style={{ background: '#F5EFE0', borderLeft: '4px solid #A07820', borderRadius: '0 10px 10px 0', padding: '1.5rem 1.8rem', margin: '2rem 0' }}>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#A07820', marginBottom: '0.2rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Lightbulb style={{ width: 11, height: 11 }} />Object Lesson
          </div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.15rem', fontWeight: 700, color: '#1E3A2F', marginBottom: '0.9rem' }}>{d.objectLesson.item}</div>
          <p style={{ fontSize: '0.95rem', color: '#3A3A3A', marginBottom: '0.6rem' }}>{d.objectLesson.howTo}</p>
          <p style={{ fontStyle: 'italic', color: '#7A2C2C', fontSize: '0.95rem' }}>→ {d.objectLesson.connection}</p>
        </div>

        <Divider />

        {/* Parental Guide */}
        <Section icon={<Users />} label="For Parents" title="Understanding Together">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
            {([
              { label: 'Little Ones · Ages 3–6', borderColor: '#A0C878', badgeColor: '#5A8A30', text: d.parentalGuide.littleOnes },
              { label: 'Children · Ages 7–11', borderColor: '#6BA8D4', badgeColor: '#2A6090', text: d.parentalGuide.children },
              { label: 'Tweens · Ages 12+', borderColor: '#C47A5A', badgeColor: '#8A3A20', text: d.parentalGuide.tweens },
            ]).map(({ label, borderColor, badgeColor, text }) => (
              <div key={label} style={{ background: 'white', borderRadius: 10, padding: '1.3rem 1.2rem', borderTop: `3px solid ${borderColor}` }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: badgeColor, marginBottom: '0.6rem' }}>{label}</div>
                <p style={{ fontSize: '0.92rem', color: '#3A3A3A', lineHeight: 1.6 }}>{text}</p>
              </div>
            ))}
          </div>
        </Section>

        <Divider />

        {/* Wonder Questions */}
        <Section icon={<Sparkles />} label="Discussion" title="Wonder Together">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginTop: '1rem' }}>
            {(d.wonderQuestions || []).map((q, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '0.9rem', alignItems: 'start', background: 'white', borderRadius: 10, padding: '1rem 1.2rem', border: '1px solid #D4C4A0' }}>
                <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5B7A6E', paddingTop: '0.1rem', lineHeight: 1.4 }}>{q.age}</span>
                <p style={{ fontStyle: 'italic', color: '#3A3A3A', fontSize: '1rem', lineHeight: 1.55 }}>{q.question}</p>
              </div>
            ))}
          </div>
        </Section>

        <Divider />

        {/* Family Application */}
        <Section icon={<MessageCircle />} label="Living It Out" title="Family Application">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <AppCard emoji="🍽" label="Dinner Table Question">
              <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: '1.12rem', color: '#1E3A2F', lineHeight: 1.5 }}>{d.familyApplication.tableQuestion}</p>
            </AppCard>
            <AppCard emoji="🎯" label="Weekly Family Challenge">
              <p style={{ fontSize: '1rem', color: '#3A3A3A' }}>{d.familyApplication.weeklyChallenge}</p>
            </AppCard>
            {d.familyApplication.conversationStarters?.length > 0 && (
              <AppCard emoji="💬" label="Conversation Starters">
                <ul style={{ paddingLeft: '1.2rem' }}>
                  {d.familyApplication.conversationStarters.map((s, i) => (
                    <li key={i} style={{ fontSize: '0.97rem', color: '#3A3A3A', marginBottom: '0.3rem' }}>{s}</li>
                  ))}
                </ul>
              </AppCard>
            )}
            <div style={{ background: '#1E3A2F', color: 'white', borderRadius: 12, padding: '1.6rem 1.8rem' }}>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#C49A30', marginBottom: '0.8rem' }}>🙏 &nbsp; Family Prayer</div>
              <p style={{ fontFamily: "'Crimson Text', serif", fontStyle: 'italic', fontSize: '1.05rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>{d.familyApplication.prayerGuide}</p>
            </div>
          </div>
        </Section>

        <Divider />

        {/* Parental Blessing */}
        <div style={{ textAlign: 'center', padding: '2.5rem 2rem', borderTop: '1px solid #D4C4A0', borderBottom: '1px solid #D4C4A0', margin: '2.5rem 0' }}>
          <span style={{ fontSize: '1.6rem', display: 'block', marginBottom: '0.9rem' }}>✦</span>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#5B7A6E', marginBottom: '1rem' }}>
            <Heart style={{ display: 'inline', width: 11, height: 11, marginRight: 6 }} />Parental Blessing
          </div>
          <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: '1.25rem', color: '#1E3A2F', lineHeight: 1.6, maxWidth: 540, margin: '0 auto' }}>"{d.parentalBlessing}"</p>
        </div>

        {/* Closing Thought */}
        {d.closingThought && (
          <div style={{ textAlign: 'center', padding: '2rem 1rem 1rem' }}>
            <blockquote style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: '1.35rem', color: '#1E3A2F', lineHeight: 1.5, maxWidth: 560, margin: '0 auto' }}>
              "{d.closingThought}"
            </blockquote>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '2rem', borderTop: '1px solid #D4C4A0', marginTop: '3rem' }}>
        <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#666' }}>
          SermonBuild &nbsp;·&nbsp; Family Devotional Guide
        </span>
      </footer>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 11pt; background: white; }
        }
        @media (max-width: 620px) {
          .age-grid { grid-template-columns: 1fr !important; }
          .mv-tips { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Section({ icon, label, title, children }: { icon: React.ReactNode; label: string; title: string; children: React.ReactNode }) {
  return (
    <section>
      <div style={{ marginBottom: '1.2rem' }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#5B7A6E', display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.35rem' }}>
          <span style={{ display: 'inline-flex', width: 14, height: 14 }}>{icon}</span>
          {label}
        </div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.55rem', fontWeight: 700, color: '#1E3A2F', lineHeight: 1.25 }}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function AppCard({ emoji, label, children }: { emoji: string; label: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: '1.4rem 1.6rem', border: '1px solid #D4C4A0' }}>
      <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#5B7A6E', marginBottom: '0.35rem' }}>{emoji} &nbsp; {label}</div>
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div style={{ textAlign: 'center', color: '#D4C4A0', fontSize: '1.2rem', letterSpacing: '0.4em', margin: '2.5rem 0', userSelect: 'none' }}>
      ✦ &nbsp;─────&nbsp; ✦ &nbsp;─────&nbsp; ✦
    </div>
  );
}
